import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Upload_Icon from "../../images/vehicle/Upload_icon.svg";
import Upload_Edit_Icon from "../../images/vehicle/Upload_Edit_icon.svg";

// Validation utility functions
const validateRequired = (value, fieldName, t) => {
  if (!value || (typeof value === "string" && value.trim() === "")) {
    return t("validation.required", { field: fieldName });
  }
  return "";
};

const validateAlphaNumeric = (value, fieldName, t) => {
  if (!value) return "";
  const regex = /^[a-zA-Z0-9\s-]*$/;
  if (!regex.test(value)) {
    return t("validation.alphaNumeric", { field: fieldName });
  }
  return "";
};

const validateYear = (value, t) => {
  if (!value) return "";
  const year = parseInt(value, 10);
  const currentYear = new Date().getFullYear();

  if (isNaN(year) || value.length !== 4) {
    return t("validation.invalidYear");
  }

  if (year < 1900 || year > currentYear + 1) {
    return t("validation.yearRange", { min: 1900, max: currentYear + 1 });
  }
  return "";
};

const validateChassisNumber = (value, t) => {
  if (!value) return "";
  const regex = /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/i;

  if (value.length !== 17) {
    return t("validation.chassisLength");
  }

  if (!regex.test(value)) {
    return t("validation.chassisInvalid");
  }
  return "";
};

const validateInsurancePolicy = (value, t) => {
  if (!value) return "";
  const regex = /^[A-Za-z0-9-/]*$/;

  if (!regex.test(value)) {
    return t("validation.insurancePolicyInvalid");
  }
  return "";
};

const validateDate = (value, fieldName, t) => {
  if (!value) return "";

  const selectedDate = new Date(value);

  if (isNaN(selectedDate.getTime())) {
    return t("validation.invalidDate", { field: fieldName });
  }
  return "";
};

const validateDescription = (value, t) => {
  if (!value) {
    return validateRequired(value, t("description"), t);
  }

  if (value.length < 10) {
    return t("validation.descriptionMinLength");
  }

  if (value.length > 500) {
    return t("validation.descriptionMaxLength");
  }
  return "";
};

// VehicleAddDetails Component
export function VehicleAddDetails({
  vehicleFormData,
  setVehicleFormData,
  selectedImage,
  setSelectedImage,
  routes = [],
  drivers = [],
  sites = [],
  errors = {},
  setErrors,
  isEditing,
  selectedSchedules,
  setSelectedSchedules,
  onValidationChange,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userFeatures, setUserFeatures] = useState([]);
  const [touched, setTouched] = useState({});

  const hasPermission = (apiPath, action) => {
    return userFeatures.some(
      (feature) => feature.api_path === apiPath && feature.action === action
    );
  };

  useEffect(() => {
    try {
      const localDataString = localStorage.getItem("userState");
      if (localDataString) {
        const localData = JSON.parse(localDataString);
        if (localData && localData.features) {
          setUserFeatures(localData.features);
        }
      }
    } catch (error) {
      console.error("Error loading features from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    // Set imagePreview for existing images when in edit mode
    if (
      isEditing &&
      selectedImage &&
      selectedImage.isExisting &&
      selectedImage.location
    ) {
      setVehicleFormData((prev) => ({
        ...prev,
        imagePreview: selectedImage.location,
      }));
    }
  }, [isEditing, selectedImage]);

  useEffect(() => {
    const isValid = validateForm();
    onValidationChange(isValid, errors);
    console.log("VehicleAddDetails Validation:", {
      isValid,
      errors,
      vehicleFormData,
    });
  }, [vehicleFormData, touched, errors, onValidationChange]);

  const validateForm = () => {
    const newErrors = {};

    // Mandatory fields
    newErrors.device_id = validateRequired(
      vehicleFormData.device_id,
      t("Vehicle ID"),
      t
    );
    newErrors.name = validateRequired(
      vehicleFormData.name,
      t("Vehicle Name"),
      t
    );
    newErrors.VehicleNumber = validateRequired(
      vehicleFormData.VehicleNumber,
      t("vehicleNumber"),
      t
    );
    newErrors.description = validateDescription(vehicleFormData.description, t);

    // Non-mandatory fields (validate only if filled)
    if (vehicleFormData.Make) {
      newErrors.Make = validateAlphaNumeric(vehicleFormData.Make, t("make"), t);
    }
    if (vehicleFormData.Model) {
      newErrors.Model = validateAlphaNumeric(
        vehicleFormData.Model,
        t("model"),
        t
      );
    }
    if (vehicleFormData.Year) {
      newErrors.Year = validateYear(vehicleFormData.Year, t);
    }
    if (vehicleFormData.chassisNumber) {
      newErrors.chassisNumber = validateChassisNumber(
        vehicleFormData.chassisNumber,
        t
      );
    }
    if (vehicleFormData.insurancePolicyNumber) {
      newErrors.insurancePolicyNumber = validateInsurancePolicy(
        vehicleFormData.insurancePolicyNumber,
        t
      );
    }
    if (vehicleFormData.insurancePolicyExpiry) {
      newErrors.insurancePolicyExpiry = validateDate(
        vehicleFormData.insurancePolicyExpiry,
        t("insurancePolicyExpiryDate"),
        t
      );
    }
    if (vehicleFormData.serviceDueDate) {
      newErrors.serviceDueDate = validateDate(
        vehicleFormData.serviceDueDate,
        t("serviceDueDate"),
        t
      );
    }
    if (vehicleFormData.reregistrationDueDate) {
      newErrors.reregistrationDueDate = validateDate(
        vehicleFormData.reregistrationDueDate,
        t("re-registrationDueDate"),
        t
      );
    }

    // Dependency: Insurance policy expiry required if number is provided
    if (
      vehicleFormData.insurancePolicyNumber &&
      !vehicleFormData.insurancePolicyExpiry
    ) {
      newErrors.insurancePolicyExpiry = t("validation.requiredWithField", {
        field: t("insurancePolicyExpiryDate"),
        dependentField: t("insurancePolicyNumber"),
      });
    }

    setErrors(newErrors);
    const isValid = !Object.values(newErrors).some((error) => error !== "");
    return isValid;
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setVehicleFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    // Validate the specific field
    let errorMessage = "";
    switch (fieldName) {
      case "device_id":
      case "name":
      case "VehicleNumber":
        errorMessage = validateRequired(value, t(fieldName), t);
        break;
      case "Make":
      case "Model":
        errorMessage = validateAlphaNumeric(value, t(fieldName), t);
        break;
      case "Year":
        errorMessage = validateYear(value, t);
        break;
      case "chassisNumber":
        errorMessage = validateChassisNumber(value, t);
        break;
      case "insurancePolicyNumber":
        errorMessage = validateInsurancePolicy(value, t);
        break;
      case "insurancePolicyExpiry":
      case "serviceDueDate":
      case "reregistrationDueDate":
        errorMessage = validateDate(value, t(fieldName), t);
        break;
      case "description":
        errorMessage = validateDescription(value, t);
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [fieldName]: errorMessage,
    }));

    // Re-validate dependent fields
    if (fieldName === "insurancePolicyNumber" && value) {
      const expiryError = vehicleFormData.insurancePolicyExpiry
        ? validateDate(
            vehicleFormData.insurancePolicyExpiry,
            t("insurancePolicyExpiryDate"),
            t
          )
        : t("validation.requiredWithField", {
            field: t("insurancePolicyExpiryDate"),
            dependentField: t("insurancePolicyNumber"),
          });

      setErrors((prev) => ({
        ...prev,
        insurancePolicyExpiry: expiryError,
      }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    validateForm();
  };

  const handleDropdownChange = (e, fieldName) => {
    const value = e.target.value;

    switch (fieldName) {
      case "route":
        const selectedRoute = routes.find(
          (route) =>
            route.id === value ||
            route.id === Number(value) ||
            route.value === value
        );
        setVehicleFormData((prev) => ({
          ...prev,
          routes: selectedRoute ? selectedRoute.id : "", // Use 'routes' to match VehicleAddMain
        }));
        break;
      case "driver":
        const selectedDriver = drivers.find((driver) => driver.id === value);
        setVehicleFormData((prev) => ({
          ...prev,
          driverId: selectedDriver ? selectedDriver.id : "", // Use 'driverId' to match VehicleAddMain
        }));
        break;
      case "site":
        setVehicleFormData((prev) => ({
          ...prev,
          sites: value,
        }));
        break;
      default:
        setVehicleFormData((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
    }

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    validateForm();
  };

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          image: t("validation.fileSizeExceeded", { max: "10MB" }),
        }));
        return;
      }

      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          image: t("validation.invalidFileType", { types: "PNG, JPG, GIF" }),
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        image: "",
      }));

      setSelectedImage(file); // Set as File object for new uploads
      const imageUrl = URL.createObjectURL(file);
      setVehicleFormData((prev) => ({
        ...prev,
        imagePreview: imageUrl,
      }));
    }
  };

  const handleRemoveImage = () => {
    if (selectedImage instanceof File && vehicleFormData.imagePreview) {
      URL.revokeObjectURL(vehicleFormData.imagePreview);
    }
    setSelectedImage(null);
    setVehicleFormData((prev) => ({
      ...prev,
      imagePreview: null,
    }));
  };

  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const handleOpenSchedule = () => {
    if (vehicleFormData.id) {
      navigate(`/vehicle/schedule?vehicleId=${vehicleFormData.id}`);
    }
  };

  const inputStyles = {
    height: 55,
    "& .MuiOutlinedInput-root": {
      height: 55,
      borderRadius: "16px",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
      border: (theme) => `2px solid ${theme.palette.grey[800]}`,
      "&.Mui-error": {
        border: "2px solid #d32f2f",
      },
    },
    "& .MuiInputBase-input": {
      padding: "16px 24px",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: "14px",
      marginTop: "3px",
      color: "#d32f2f",
    },
  };

  const selectStyles = {
    "& .MuiSelect-select": {
      borderRadius: "16px",
      border: (theme) => `2px solid ${theme.palette.grey[800]}`,
      padding: "16px 24px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-error .MuiSelect-select": {
      border: "2px solid #d32f2f",
    },
  };

  const RequiredLabel = ({ label }) => (
    <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        color="error"
        sx={{ ml: 0.5 }}
      >
        *
      </Typography>
    </Box>
  );

  const StandardLabel = ({ label }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
        margin: { xs: "0px 20px", md: "0px 40px 0px 40px" },
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        borderRadius: "10px",
        height: "100%",
        boxShadow: "-2px 2px 5px 0px #00000040",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Paper
          sx={{
            height: 52,
            width: isEditing ? "calc(66.67% - 15px)" : "66.77%",
            marginRight: "20px",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            color="white"
            sx={{ fontSize: { xs: "18px", md: "20px" } }}
          >
            {t("vehicleDetails")}
          </Typography>
        </Paper>

        {isEditing ? (
          hasPermission("/api/schedule/vehicle", "PUT") && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenSchedule}
              sx={{
                width: 224,
                height: 47,
                borderRadius: "8px",
                gap: "10px",
                padding: "12px 24px",
                background: "#F9E719",
                textTransform: "none",
                color: "#1F242E",
                "&:hover": {
                  background: "#F9E719",
                },
              }}
            >
              {t("assignSchedules")}
            </Button>
          )
        ) : (
          <Button
            variant="contained"
            disabled
            sx={{
              width: 224,
              height: 47,
              borderRadius: "8px",
              gap: "10px",
              padding: "12px 24px",
              background: "#F2F2F3",
              textTransform: "none",
              color: "black !important",
              border: "2px solid black",
              cursor: "not-allowed",
            }}
          >
            {t("assignSchedules")}
          </Button>
        )}
      </Box>
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* First row */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mb={1}>
                  <RequiredLabel label={t("Vehicle ID")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Vehicle ID")}
                    value={vehicleFormData.device_id || ""}
                    onChange={(e) => handleInputChange(e, "device_id")}
                    onBlur={() => handleBlur("device_id")}
                    error={!!(touched.device_id && errors.device_id)}
                    helperText={
                      touched.device_id && errors.device_id
                        ? errors.device_id
                        : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6} mb={1}>
                  <RequiredLabel label={t("Vehicle Name")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Vehicle Name")}
                    value={vehicleFormData.name || ""}
                    onChange={(e) => handleInputChange(e, "name")}
                    onBlur={() => handleBlur("name")}
                    error={!!(touched.name && errors.name)}
                    helperText={touched.name && errors.name ? errors.name : ""}
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>

              {/* Second row */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mb={1.5}>
                  <RequiredLabel label={t("vehicleNumber")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Vehicle Number")}
                    value={vehicleFormData.VehicleNumber || ""}
                    onChange={(e) => handleInputChange(e, "VehicleNumber")}
                    onBlur={() => handleBlur("VehicleNumber")}
                    error={!!(touched.VehicleNumber && errors.VehicleNumber)}
                    helperText={
                      touched.VehicleNumber && errors.VehicleNumber
                        ? errors.VehicleNumber
                        : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6} mb={1.5}>
                  <StandardLabel label={t("make")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Make Details")}
                    value={vehicleFormData.Make || ""}
                    onChange={(e) => handleInputChange(e, "Make")}
                    onBlur={() => handleBlur("Make")}
                    error={!!(touched.Make && errors.Make)}
                    helperText={touched.Make && errors.Make ? errors.Make : ""}
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mb={1.5}>
                  <StandardLabel label={t("model")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Model")}
                    value={vehicleFormData.Model || ""}
                    onChange={(e) => handleInputChange(e, "Model")}
                    onBlur={() => handleBlur("Model")}
                    error={!!(touched.Model && errors.Model)}
                    helperText={
                      touched.Model && errors.Model ? errors.Model : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6} mb={1.5}>
                  <StandardLabel label={t("chassisNumber")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Chassis Number")}
                    value={vehicleFormData.chassisNumber || ""}
                    onChange={(e) => handleInputChange(e, "chassisNumber")}
                    onBlur={() => handleBlur("chassisNumber")}
                    error={!!(touched.chassisNumber && errors.chassisNumber)}
                    helperText={
                      touched.chassisNumber && errors.chassisNumber
                        ? errors.chassisNumber
                        : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mb={1}>
                  <StandardLabel label={t("yearofManufacture")} />
                  <TextField
                    fullWidth
                    type="text"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder="YYYY"
                    value={vehicleFormData.Year || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,4}$/.test(value)) {
                        handleInputChange(e, "Year");
                      }
                    }}
                    inputProps={{
                      maxLength: 4,
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                    }}
                    onBlur={() => handleBlur("Year")}
                    error={!!(touched.Year && errors.Year)}
                    helperText={touched.Year && errors.Year ? errors.Year : ""}
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}></Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                width: "100%",
                border: errors.image
                  ? "2px dashed #d32f2f"
                  : "2px dashed #183059",
                borderRadius: 2,
                height: 350,
                backgroundColor: "#E8EAEE",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {vehicleFormData.imagePreview ? (
                <Box
                  sx={{ position: "relative", width: "100%", height: "100%" }}
                >
                  <img
                    src={vehicleFormData.imagePreview}
                    alt="Vehicle"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "white",
                    }}
                    size="small"
                  >
                    <img src={Upload_Edit_Icon} alt="Upload Edit" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <img
                    src={Upload_Icon}
                    alt="Upload"
                    style={{ width: 50, height: 50 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 2,
                      color: "#1F242E",
                      fontFamily: "Roboto",
                      fontWeight: 600,
                      fontSize: "20px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      textAlign: "center",
                    }}
                  >
                    {t("vehicleImage")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
                    {t("Click to upload or drag and drop")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("PNG, JPG, GIF up to 10MB")}
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                </>
              )}
            </Box>
            {errors.image && (
              <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                {errors.image}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {hasPermission("/api/route/organization", "GET") && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("route")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.routes && errors.routes)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.routes || ""}
                    onChange={(e) => handleDropdownChange(e, "route")}
                    onBlur={() => handleBlur("routes")}
                    inputProps={{ "aria-label": "Route" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Route")}
                          </Typography>
                        );
                      }
                      const selectedRoute = routes.find(
                        (route) => route.id === selected
                      );
                      return selectedRoute ? selectedRoute.name : "";
                    }}
                    sx={selectStyles}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {routes.map((route) => (
                      <MenuItem key={route.id} value={route.id}>
                        {route.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.routes && errors.routes && (
                    <FormHelperText error>{errors.routes}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}

            {hasPermission("/api/driver/organization", "GET") && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("driver")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.driverId && errors.driverId)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.driverId || ""}
                    onChange={(e) => handleDropdownChange(e, "driver")}
                    onBlur={() => handleBlur("driverId")}
                    inputProps={{ "aria-label": "Driver" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Driver")}
                          </Typography>
                        );
                      }
                      const selectedDriver = drivers.find(
                        (driver) => driver.id === selected
                      );
                      return selectedDriver
                        ? `${selectedDriver.first_name} ${selectedDriver.last_name}`
                        : "";
                    }}
                    sx={selectStyles}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {drivers.map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.first_name} {driver.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.driverId && errors.driverId && (
                    <FormHelperText error>{errors.driverId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            {hasPermission("/api/site/organization", "GET") && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("site")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.sites && errors.sites)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.sites || ""}
                    onChange={(e) => handleDropdownChange(e, "site")}
                    onBlur={() => handleBlur("sites")}
                    inputProps={{ "aria-label": "Site" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Site")}
                          </Typography>
                        );
                      }
                      const selectedSite = sites.find(
                        (site) => site.id === selected
                      );
                      return selectedSite ? selectedSite.siteId : "";
                    }}
                    sx={selectStyles}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.siteId}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.sites && errors.sites && (
                    <FormHelperText error>{errors.sites}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("insurancePolicyNumber")} />
              <TextField
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                placeholder={t("Type Policy Number")}
                value={vehicleFormData.insurancePolicyNumber || ""}
                onChange={(e) => handleInputChange(e, "insurancePolicyNumber")}
                onBlur={() => handleBlur("insurancePolicyNumber")}
                error={
                  !!(
                    touched.insurancePolicyNumber &&
                    errors.insurancePolicyNumber
                  )
                }
                helperText={
                  touched.insurancePolicyNumber && errors.insurancePolicyNumber
                    ? errors.insurancePolicyNumber
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("insurancePolicyExpiryDate")} />
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                value={
                  formatDateForInput(vehicleFormData.insurancePolicyExpiry) ||
                  ""
                }
                onChange={(e) => handleInputChange(e, "insurancePolicyExpiry")}
                onBlur={() => handleBlur("insurancePolicyExpiry")}
                error={
                  !!(
                    touched.insurancePolicyExpiry &&
                    errors.insurancePolicyExpiry
                  )
                }
                helperText={
                  touched.insurancePolicyExpiry && errors.insurancePolicyExpiry
                    ? errors.insurancePolicyExpiry
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("serviceDueDate")} />
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                value={formatDateForInput(vehicleFormData.serviceDueDate) || ""}
                onChange={(e) => handleInputChange(e, "serviceDueDate")}
                onBlur={() => handleBlur("serviceDueDate")}
                error={!!(touched.serviceDueDate && errors.serviceDueDate)}
                helperText={
                  touched.serviceDueDate && errors.serviceDueDate
                    ? errors.serviceDueDate
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("re-registrationDueDate")} />
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                value={formatDateForInput(vehicleFormData.reRegistration) || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setVehicleFormData((prev) => ({
                    ...prev,
                    reRegistration: value,
                    reregistrationDueDate: value,
                  }));
                  setTouched((prev) => ({
                    ...prev,
                    reregistrationDueDate: true,
                  }));
                }}
                onBlur={() => handleBlur("reregistrationDueDate")}
                error={
                  !!(
                    touched.reregistrationDueDate &&
                    errors.reregistrationDueDate
                  )
                }
                helperText={
                  touched.reregistrationDueDate && errors.reregistrationDueDate
                    ? errors.reregistrationDueDate
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RequiredLabel label={t("description")} />
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                placeholder={t("Type Description")}
                value={vehicleFormData.description || ""}
                onChange={(e) => handleInputChange(e, "description")}
                onBlur={() => handleBlur("description")}
                error={!!(touched.description && errors.description)}
                helperText={
                  touched.description && errors.description
                    ? errors.description
                    : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...inputStyles["& .MuiOutlinedInput-root"],
                    height: "auto",
                    "& .MuiInputBase-input": {
                      height: "auto",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default VehicleAddDetails;
